import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Users,
  Briefcase,
  Bell,
  FileCheck2,
  Search,
  ArrowRight,
  CheckCircle2,
  Star,
  MessageSquare,
  ClipboardList,
  Sparkles,
  BookOpen,
  PlayCircle,
  Layers3,
  Settings2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import api from '../lib/api';

export default function LandingPage() {
  const { user, loading } = useAuth();

  const role = user?.profile_type || user?.type;
    const [featuredResources, setFeaturedResources] = useState([]);
const [resourcesLoading, setResourcesLoading] = useState(true);

useEffect(() => {
  const fetchFeaturedResources = async () => {
    try {
      const res = await api.get('/public/resources?limit=6');
      setFeaturedResources(res.data?.data || []);
    } catch (err) {
      setFeaturedResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  fetchFeaturedResources();
}, []);

  const dashboardPath =
    role === 'nanny'
      ? '/dashboard/nanny'
      : role === 'agent'
      ? '/dashboard/agent'
      : role === 'customer'
      ? '/dashboard/customer'
      : role === 'admin'
      ? '/dashboard/admin'
      : '/login';

  const features = [
    {
      icon: Search,
      title: 'Discover trusted domestic workers',
      text: 'Browse profiles with experience, qualifications, availability, vetting signals, and work history in one place.',
    },
    {
      icon: ShieldCheck,
      title: 'Built for trust',
      text: 'KYC, guarantor details, vetting workflows, and admin oversight help make hiring safer and more accountable.',
    },
    {
      icon: Briefcase,
      title: 'Structured hiring flow',
      text: 'Move from profile discovery to offer, negotiation, contract, and support without leaving the platform.',
    },
    {
      icon: Bell,
      title: 'Stay updated',
      text: 'Notifications keep customers, workers, agents, and admins informed across every key action.',
    },
    {
      icon: FileCheck2,
      title: 'Contract-ready operations',
      text: 'Accepted offers flow into contract management so engagements are clearer and easier to manage.',
    },
    {
      icon: MessageSquare,
      title: 'Support when issues happen',
      text: 'Complaints, disputes, and appeals are part of the system, not an afterthought.',
    },
    {
      icon: BookOpen,
      title: 'Premium learning resources',
      text: 'Offer paid books, downloadable content, video files, and protected video links for workers, customers, or both.',
    },
    {
      icon: Settings2,
      title: 'Admin resource management',
      text: 'Admins can upload, publish, organize, and manage paid or free learning resources for the right audience.',
    },
  ];

  const personas = [
    {
      title: 'For Employers & Families',
      text: 'Find, assess, and hire domestic workers with more confidence using verified information, working schedules, and structured offers.',
      bullets: [
        'Browse worker profiles',
        'Send offers and negotiate terms',
        'Track current contracts and support issues',
      ],
    },
    {
      title: 'For Domestic Workers',
      text: 'Create a strong professional profile that showcases your experience, availability, qualifications, trust signals, and learning progress.',
      bullets: [
        'Complete your profile',
        'Receive and respond to offers',
        'Access paid and protected training resources',
      ],
    },
    {
      title: 'For Agents & Admins',
      text: 'Coordinate vetting, assignments, moderation, resources, and platform oversight from one operational system.',
      bullets: [
        'Assign and vet workers',
        'Manage disputes and complaints',
        'Upload and control educational resources',
      ],
    },
  ];

  const steps = [
    {
      icon: ClipboardList,
      title: 'Create your account',
      text: 'Sign up as a customer, worker, agent, or admin and enter the platform with the right workflow.',
    },
    {
      icon: Sparkles,
      title: 'Build trust and visibility',
      text: 'Workers complete profiles, upload supporting details, and become easier to discover and assess.',
    },
    {
      icon: Briefcase,
      title: 'Hire with structure',
      text: 'Customers send offers, both sides negotiate if needed, and accepted offers become active engagements.',
    },
  ];

  const resourceHighlights = [
    {
      icon: BookOpen,
      title: 'Books & downloadable guides',
      text: 'Sell or share professional guides, ebooks, training manuals, and downloadable worker content.',
    },
    {
      icon: PlayCircle,
      title: 'Video learning',
      text: 'Provide uploaded videos or protected external video links that only paid users can access.',
    },
    {
      icon: Layers3,
      title: 'Audience-based delivery',
      text: 'Publish resources for workers, customers, or both depending on who the content is meant for.',
    },
  ];

  const stats = [
    { value: '4 roles', label: 'Customers, Workers, Agents, Admins' },
    { value: 'End-to-end', label: 'From discovery to support' },
    { value: 'Trust-first', label: 'Vetting, KYC, guarantor flow' },
  ];

  const HeaderActions = () => {
    if (loading) {
      return (
        <div className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-500">
          Loading...
        </div>
      );
    }

    if (user) {
      return (
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/profile"
            className="rounded-xl border border-primary/15 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary/5"
          >
            View profile
          </Link>
          <Link
            to={dashboardPath}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
          >
            Go to dashboard
          </Link>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <Link
          to="/login"
          className="rounded-xl border border-primary/15 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary/5"
        >
          Sign in
        </Link>
        <Link
          to="/register"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
        >
          Get started
        </Link>
      </div>
    );
  };

  const HeroActions = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="inline-flex items-center justify-center rounded-2xl border border-black/10 bg-white px-6 py-3.5 text-sm font-bold text-text">
            Loading...
          </div>
        </div>
      );
    }

    if (user) {
      return (
        <div className="flex flex-col gap-4 sm:flex-row">
          <Link
            to={dashboardPath}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:translate-y-[-1px]"
          >
            Go to dashboard
            <ArrowRight size={18} />
          </Link>

          <Link
            to="/dashboard/resources"
            className="inline-flex items-center justify-center rounded-2xl border border-black/10 bg-white px-6 py-3.5 text-sm font-bold text-text transition hover:border-primary/25 hover:text-primary"
          >
            Explore resources
          </Link>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4 sm:flex-row">
        <Link
          to="/register"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:translate-y-[-1px]"
        >
          Create account
          <ArrowRight size={18} />
        </Link>

        <Link
          to="/login"
          className="inline-flex items-center justify-center rounded-2xl border border-black/10 bg-white px-6 py-3.5 text-sm font-bold text-text transition hover:border-primary/25 hover:text-primary"
        >
          Sign in to dashboard
        </Link>
      </div>
    );
  };

  const BottomActions = () => {
    if (loading) {
      return (
        <div className="inline-flex items-center justify-center rounded-2xl border border-black/10 bg-white px-6 py-3.5 text-sm font-bold text-text">
          Loading...
        </div>
      );
    }

    if (user) {
      return (
        <>
          <Link
            to={dashboardPath}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:translate-y-[-1px]"
          >
            Go to dashboard
            <ArrowRight size={18} />
          </Link>
          <Link
            to="/dashboard/resources"
            className="inline-flex items-center justify-center rounded-2xl border border-black/10 bg-white px-6 py-3.5 text-sm font-bold text-text transition hover:border-primary/25 hover:text-primary"
          >
            Explore resources
          </Link>
        </>
      );
    }

    return (
      <>
        <Link
          to="/register"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:translate-y-[-1px]"
        >
          Create account
          <ArrowRight size={18} />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center justify-center rounded-2xl border border-black/10 bg-white px-6 py-3.5 text-sm font-bold text-text transition hover:border-primary/25 hover:text-primary"
        >
          Sign in
        </Link>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background text-text">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="AuthWorka" className="h-11 w-auto" />
            <div className="hidden sm:block">
              <p className="text-lg font-black tracking-tight text-primary">AuthWorka</p>
              <p className="text-xs text-muted">Trusted domestic workforce operations</p>
            </div>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            <a href="#features" className="text-sm font-medium text-muted transition hover:text-primary">
              Features
            </a>
            <a href="#resources" className="text-sm font-medium text-muted transition hover:text-primary">
              Resources
            </a>
            <a href="#how-it-works" className="text-sm font-medium text-muted transition hover:text-primary">
              How it works
            </a>
            <a href="#who-its-for" className="text-sm font-medium text-muted transition hover:text-primary">
              Who it’s for
            </a>
            <Link to="/terms" className="text-sm font-medium text-muted transition hover:text-primary">
              Terms
            </Link>
            <Link to="/privacy-policy" className="text-sm font-medium text-muted transition hover:text-primary">
              Privacy
            </Link>
          </nav>

          <HeaderActions />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-white to-secondary/10" />
        <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/10 bg-white/90 px-4 py-2 text-sm font-medium text-primary shadow-sm">
              <ShieldCheck size={16} />
              Safer hiring. Clearer workflows. Better oversight.
            </div>

            <h1 className="max-w-3xl text-4xl font-black leading-tight tracking-tight text-text sm:text-5xl lg:text-6xl">
              Hire and manage trusted domestic workers with confidence.
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-8 text-muted sm:text-lg">
              AuthWorka helps employers discover domestic workers, helps workers build strong
              professional profiles, gives agents and admins the tools to manage vetting,
              contracts, support, and now supports protected paid resources like books,
              videos, and training content.
            </p>

            <div className="mt-8">
              <HeroActions />
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {stats.map((item) => (
                <div key={item.label} className="rounded-2xl border border-black/5 bg-white/80 p-4 shadow-sm">
                  <p className="text-xl font-black text-primary">{item.value}</p>
                  <p className="mt-1 text-sm text-muted">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -left-6 -top-6 hidden rounded-2xl bg-white p-4 shadow-xl lg:block">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-secondary/10 p-2 text-secondary">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <p className="text-sm font-bold text-text">Profile-driven matching</p>
                  <p className="text-xs text-muted">Experience, schedule, trust signals</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -right-6 hidden rounded-2xl bg-white p-4 shadow-xl lg:block">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                  <BookOpen size={18} />
                </div>
                <div>
                  <p className="text-sm font-bold text-text">Protected learning content</p>
                  <p className="text-xs text-muted">Paid books, videos, and training resources</p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-black/5 bg-white shadow-2xl shadow-black/5">
              <img
                src="/images/nanny.jpg"
                alt="Domestic worker and child"
                className="h-[420px] w-full object-cover lg:h-[560px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-black/5 bg-surface">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-6 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-primary" size={20} />
            <span className="text-sm font-medium text-text">Vetting-aware workflows</span>
          </div>
          <div className="flex items-center gap-3">
            <Users className="text-primary" size={20} />
            <span className="text-sm font-medium text-text">Role-based user journeys</span>
          </div>
          <div className="flex items-center gap-3">
            <Briefcase className="text-primary" size={20} />
            <span className="text-sm font-medium text-text">Offer to contract lifecycle</span>
          </div>
          <div className="flex items-center gap-3">
            <BookOpen className="text-primary" size={20} />
            <span className="text-sm font-medium text-text">Protected paid resources</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">Why AuthWorka</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
            A platform built around trust, structure, and accountability.
          </h2>
          <p className="mt-4 text-base leading-8 text-muted">
            AuthWorka is more than a listing platform. It is a system for discovery, assessment,
            hiring, learning, operational oversight, and issue resolution.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm transition hover:translate-y-[-4px] hover:shadow-lg"
              >
                <div className="inline-flex rounded-2xl bg-primary/10 p-3 text-primary">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 text-xl font-bold text-text">{feature.title}</h3>
                <p className="mt-3 text-sm leading-7 text-muted">{feature.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section id="resources" className="bg-surface">
  <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
    <div className="mx-auto max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">Resource Library</p>
      <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
        Real learning resources, powered by live platform content.
      </h2>
      <p className="mt-4 text-base leading-8 text-muted">
        AuthWorka supports paid and protected books, downloadable guides, video files,
        and video links for domestic workers, customers, or both.
      </p>
    </div>

    <div className="mt-14">
      {resourcesLoading ? (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm animate-pulse"
            >
              <div className="h-44 w-full rounded-2xl bg-gray-200" />
              <div className="mt-4 h-4 w-20 rounded bg-gray-200" />
              <div className="mt-3 h-6 w-3/4 rounded bg-gray-200" />
              <div className="mt-3 h-4 w-full rounded bg-gray-200" />
              <div className="mt-2 h-4 w-5/6 rounded bg-gray-200" />
              <div className="mt-6 flex items-center justify-between">
                <div className="h-6 w-20 rounded bg-gray-200" />
                <div className="h-10 w-28 rounded-xl bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      ) : featuredResources.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {featuredResources.map((resource) => (
            <div
              key={resource.id}
              className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm transition hover:translate-y-[-4px] hover:shadow-lg"
            >
              {resource.cover_image_url ? (
                <img
                  src={resource.cover_image_url}
                  alt={resource.title}
                  className="mb-4 h-44 w-full rounded-2xl object-cover"
                />
              ) : (
                <div className="mb-4 flex h-44 w-full items-center justify-center rounded-2xl bg-primary/5">
                  <BookOpen className="text-primary" size={36} />
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
                    {resource.content_type}
                  </span>
                  <h3 className="mt-3 text-lg font-black text-text">{resource.title}</h3>
                </div>

                <span className="rounded-full bg-secondary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-secondary">
                  {resource.audience}
                </span>
              </div>

              <p className="mt-3 text-sm leading-6 text-muted">
                {resource.summary || 'Professional training and learning material available on the platform.'}
              </p>

              <div className="mt-6 flex items-center justify-between">
                <span className="text-lg font-black text-primary">
                  {resource.price > 0 ? `₦${resource.price.toLocaleString()}` : 'Free'}
                </span>

                {user ? (
                  <Link
                    to="/dashboard/resources"
                    className="rounded-xl border border-primary/15 bg-white px-4 py-2 text-sm font-bold text-primary transition hover:bg-primary/5"
                  >
                    View library
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="rounded-xl border border-primary/15 bg-white px-4 py-2 text-sm font-bold text-primary transition hover:bg-primary/5"
                  >
                    Get access
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-[2rem] border border-dashed border-primary/20 bg-white p-10 text-center shadow-sm">
          <BookOpen className="mx-auto text-primary" size={36} />
          <h3 className="mt-4 text-2xl font-black text-text">Resource library coming alive</h3>
          <p className="mt-3 text-sm leading-7 text-muted">
            Published resources will appear here automatically once added from the admin resource management area.
          </p>
        </div>
      )}
    </div>

    <div className="mt-10 text-center">
      {user ? (
        <Link
          to="/dashboard/resources"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:translate-y-[-1px]"
        >
          Explore full library
          <ArrowRight size={18} />
        </Link>
      ) : (
        <Link
          to="/register"
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:translate-y-[-1px]"
        >
          Create account to access resources
          <ArrowRight size={18} />
        </Link>
      )}
    </div>
  </div>
</section>

      {/* How it works */}
      <section id="how-it-works" className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">How it works</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
            From onboarding to active engagement.
          </h2>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="rounded-3xl bg-white p-7 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="inline-flex rounded-2xl bg-secondary/10 p-3 text-secondary">
                    <Icon size={22} />
                  </div>
                  <span className="text-sm font-black text-primary/60">0{index + 1}</span>
                </div>
                <h3 className="mt-6 text-xl font-bold">{step.title}</h3>
                <p className="mt-3 text-sm leading-7 text-muted">{step.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Personas */}
      <section id="who-its-for" className="bg-surface">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">Who it’s for</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
              Designed for every side of the workflow.
            </h2>
          </div>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {personas.map((persona) => (
              <div key={persona.title} className="rounded-3xl border border-black/5 bg-white p-7 shadow-sm">
                <h3 className="text-2xl font-black text-text">{persona.title}</h3>
                <p className="mt-4 text-sm leading-7 text-muted">{persona.text}</p>

                <div className="mt-6 space-y-3">
                  {persona.bullets.map((bullet) => (
                    <div key={bullet} className="flex items-start gap-3">
                      <CheckCircle2 size={18} className="mt-0.5 text-secondary" />
                      <span className="text-sm text-text">{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlight */}
      <section className="bg-gradient-to-r from-primary to-secondary">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="grid items-center gap-8 lg:grid-cols-[1.5fr_1fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-white/80">Why this matters</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                Domestic workforce hiring should feel safer, clearer, and better managed.
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-8 text-white/90">
                AuthWorka combines profile strength, trust signals, hiring structure, support
                processes, and now protected learning resources into one operational platform so
                users are not left navigating critical decisions blindly.
              </p>
            </div>

            <div className="rounded-3xl bg-white/10 p-6 backdrop-blur">
              <div className="grid gap-4">
                <div className="rounded-2xl bg-white/10 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <Star size={18} />
                    <span className="font-semibold">Trust-centered onboarding</span>
                  </div>
                </div>
                <div className="rounded-2xl bg-white/10 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <Briefcase size={18} />
                    <span className="font-semibold">Structured engagement lifecycle</span>
                  </div>
                </div>
                <div className="rounded-2xl bg-white/10 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <BookOpen size={18} />
                    <span className="font-semibold">Protected education and training</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="rounded-[2rem] border border-black/5 bg-surface p-8 shadow-sm sm:p-12">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary">Get started</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
              Join AuthWorka and move with more clarity.
            </h2>
            <p className="mt-4 text-base leading-8 text-muted">
              Create your account and step into a more structured way to discover, hire, manage,
              train, or support trusted domestic worker relationships.
            </p>

            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
              <BottomActions />
            </div>

            {!user && (
              <p className="mt-6 text-xs leading-6 text-muted">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="font-semibold text-primary hover:underline">
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="font-semibold text-primary hover:underline">
                  Privacy Policy
                </Link>.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/5 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8 text-sm text-muted md:flex-row md:items-center md:justify-between lg:px-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="AuthWorka" className="h-9 w-auto" />
            <span>© {new Date().getFullYear()} AuthWorka. All rights reserved.</span>
          </div>

          <div className="flex flex-wrap items-center gap-5">
            <a href="#features" className="transition hover:text-primary">Features</a>
            <a href="#resources" className="transition hover:text-primary">Resources</a>
            <a href="#how-it-works" className="transition hover:text-primary">How it works</a>
            <a href="#who-its-for" className="transition hover:text-primary">Who it’s for</a>
            <Link to="/terms" className="transition hover:text-primary">Terms</Link>
            <Link to="/privacy-policy" className="transition hover:text-primary">Privacy Policy</Link>
            {user ? (
              <Link to={dashboardPath} className="transition hover:text-primary">
                Dashboard
              </Link>
            ) : (
              <Link to="/login" className="transition hover:text-primary">
                Login
              </Link>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}